import { Handoff } from './contracts';

/** The server's hand-off is the capability source: a method with a URL to open is redirectable. */
export type HandoffShape = Pick<Handoff, 'provider' | 'providerLabel' | 'url' | 'nativeUrl'>;

/** What the second screen does for one method (#43 follow-up). Every field is derived from the hand-off
 *  the server returns, so a future method slots in without touching the review's state machine. */
export type HandoffCapability = {
  /** The payer is sent to another app or page and must come back here. */
  redirectable: boolean;
  /** The one action on the hand-off screen: `Continue with {Method}` or `Got it`. */
  cta: string;
  /** The same action while the tap is being carried out. Only a redirectable method waits on its tap (the
   *  server confirms the attempt before the launch); `Got it` advances at once, so its busy copy is never shown. */
  ctaBusy: string;
  /** Loader line: what the payer does next, shown while the attempt is prepared. */
  nextStep: string;
  /** The hand-off screen's helper line. */
  helper: string;
  /** The receiver's details (owner, ID, Copy) are shown on the hand-off screen. */
  showsDetails: boolean;
  /** After the action the sheet waits for the payer to come back before asking Did you pay already?
   *  A method paid elsewhere goes straight there, with no wait and no loader (#43 follow-up). */
  awaitsReturn: boolean;
};

/** Does this hand-off open an app or page? Manual methods (Zelle, Apple Cash, …) are paid in the payer's own app. */
export function launchesApp(handoff: Pick<Handoff, 'url' | 'nativeUrl'>) {
  return !!(handoff.url || handoff.nativeUrl);
}

/** Where a manual method is paid from: the only per-provider wording on the second screen. */
function payerApp(handoff: HandoffShape) {
  return handoff.provider === 'zelle' ? 'your bank’s app' : handoff.providerLabel;
}

/** Method-specific helper line on the hand-off screen. */
export function handoffHelper(handoff: HandoffShape, amountLabel: string) {
  if (launchesApp(handoff)) return `You’ll be redirected to ${handoff.providerLabel}. Come back here to finish the payment.`;
  if (handoff.provider === 'zelle') return `Open your bank’s app and Zelle ${amountLabel} to this number.`;
  return `Open ${handoff.providerLabel} and send ${amountLabel} to these details.`;
}

export function handoffCapability(handoff: HandoffShape, amountLabel: string): HandoffCapability {
  const label = handoff.providerLabel;
  if (launchesApp(handoff)) return {
    redirectable: true,
    cta: `Continue with ${label}`,
    ctaBusy: `Opening ${label}…`,
    nextStep: `Next, continue to ${label} to pay, then come back here to finish.`,
    helper: handoffHelper(handoff, amountLabel),
    showsDetails: false,
    awaitsReturn: true,
  };
  return {
    redirectable: false,
    cta: 'Got it',
    ctaBusy: 'Got it',
    nextStep: `Next, copy the ${label} details and pay from ${payerApp(handoff)}, then come back here.`,
    helper: handoffHelper(handoff, amountLabel),
    showsDetails: true,
    awaitsReturn: false,
  };
}
